import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * OAuth2 Authentication Service
 * Uses Authentik for authentication with session-based auth (httpOnly cookies)
 */
export const authService = {
  /**
   * Initiate OAuth2 login flow
   * Redirects user to Authentik login page
   */
  login: () => {
    // Redirect to backend which will redirect to Authentik
    window.location.href = `${API_URL}/auth/login`;
  },

  /**
   * Logout current user
   * Destroys session on backend
   */
  logout: async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, {
        withCredentials: true
      });

      // Clear any local state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect even if logout fails
      window.location.href = '/';
    }
  },

  /**
   * Check if user has active session
   * @returns {Promise<boolean>}
   */
  checkSession: async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/session`, {
        withCredentials: true
      });
      return response.data.authenticated;
    } catch (error) {
      console.error('Session check error:', error);
      return false;
    }
  },

  /**
   * Get current user profile from backend
   * @returns {Promise<Object|null>}
   */
  getCurrentUser: async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/profile`, {
        withCredentials: true
      });

      if (response.data.success) {
        return response.data.user;
      }
      return null;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Not authenticated
        return null;
      }
      console.error('Get current user error:', error);
      return null;
    }
  },

  /**
   * Check if user is authenticated (synchronous)
   * Note: This checks for session cookie existence, but doesn't validate it
   * Use checkSession() for actual validation
   * @returns {boolean}
   */
  isAuthenticated: () => {
    // Cookie is httpOnly so we can't check it from JS
    // Return true optimistically - actual validation happens via checkSession()
    // This is only used for quick synchronous checks before async validation
    return true;
  },

  /**
   * Refresh user profile
   * @returns {Promise<Object|null>}
   */
  refreshUserProfile: async () => {
    return await authService.getCurrentUser();
  },

  /**
   * Handle OAuth2 callback (if needed for SPA routing)
   * In most cases, backend handles this automatically
   */
  handleCallback: async () => {
    // Check if we're on the callback URL
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      throw new Error(error);
    }

    // Backend handles the callback, just check session
    return await authService.checkSession();
  }
};

export default authService;
